import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import { toast } from 'react-hot-toast';
import { Eye, X } from 'lucide-react';

const AdminJobForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(false);
  const [showSalary, setShowSalary] = useState(false);
  const [preview, setPreview] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    title: '',
    description: '',
    requirements: '',
    responsibilities: '',
    skills: '',
    country: '',
    city: '',
    remote: false,
    employmentType: 'full-time',
    experienceLevel: '',
    minSalary: '',
    maxSalary: '',
    currency: 'USD',
    status: 'active'
  });

  useEffect(() => {
    if (isEdit) {
      fetchJob();
    }
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await jobsAPI.getById(id!);
      const job = response.data.data;
      setFormData({
        companyName: job.companyName || '',
        title: job.title,
        description: job.description,
        requirements: job.requirements?.join('\n') || '',
        responsibilities: job.responsibilities?.join('\n') || '',
        skills: job.skills?.join(', ') || '',
        country: job.location?.country || '',
        city: job.location?.city || '',
        remote: job.location?.remote || false,
        employmentType: job.employmentType,
        experienceLevel: job.experienceLevel || '',
        minSalary: job.salary?.min || '',
        maxSalary: job.salary?.max || '',
        currency: job.salary?.currency || 'USD',
        status: job.status || 'active'
      });
      setShowSalary(!!job.salary);
    } catch (error) {
      console.error('Error fetching job:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const jobData = {
        companyName: formData.companyName,
        title: formData.title,
        description: formData.description,
        requirements: formData.requirements.split('\n').filter(r => r.trim()),
        responsibilities: formData.responsibilities.split('\n').filter(r => r.trim()),
        skills: formData.skills.split(',').map(s => s.trim()).filter(s => s),
        location: {
          country: formData.country,
          city: formData.city,
          remote: formData.remote,
        },
        employmentType: formData.employmentType,
        experienceLevel: formData.experienceLevel || undefined,
        salary: showSalary && formData.minSalary ? {
          min: Number(formData.minSalary),
          max: Number(formData.maxSalary) || Number(formData.minSalary),
          currency: formData.currency,
          negotiable: !formData.maxSalary
        } : undefined,
        status: formData.status
      };

      if (isEdit) {
        await jobsAPI.update(id!, jobData);
      } else {
        await jobsAPI.create(jobData);
      }

      toast.success(isEdit ? 'Job updated successfully' : 'Job created successfully');
      navigate('/admin/jobs');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{isEdit ? 'Edit Job' : 'Create Job'}</h1>
            <p className="text-gray-600">Fill in the job details</p>
          </div>
          <button type="button" onClick={() => setPreview(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium">
            <Eye className="w-4 h-4" /> Preview
          </button>
        </div>

        <div className="max-w-4xl bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. RwandaTel"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Senior Software Engineer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                rows={5}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Describe the role..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Requirements (one per line)</label>
                <textarea
                  rows={4}
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Bachelor's degree&#10;5+ years experience"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Responsibilities (one per line)</label>
                <textarea
                  rows={4}
                  value={formData.responsibilities}
                  onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Design features&#10;Code reviews"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Skills (comma separated)</label>
              <input
                type="text"
                value={formData.skills}
                onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="JavaScript, React, Node.js"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Kenya"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nairobi"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.remote}
                onChange={(e) => setFormData({ ...formData, remote: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Remote work available</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
                <select
                  value={formData.employmentType}
                  onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
                <select
                  value={formData.experienceLevel}
                  onChange={(e) => setFormData({ ...formData, experienceLevel: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select level</option>
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Salary Information</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showSalary}
                    onChange={(e) => setShowSalary(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Specify salary</span>
                </label>
              </div>

              {showSalary && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Salary {formData.maxSalary ? 'Range (Min)' : ''}
                    </label>
                    <input
                      type="number"
                      value={formData.minSalary}
                      onChange={(e) => setFormData({ ...formData, minSalary: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="50000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Salary (Optional)
                    </label>
                    <input
                      type="number"
                      value={formData.maxSalary}
                      onChange={(e) => setFormData({ ...formData, maxSalary: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="80000"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty for negotiable</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="USD">USD</option>
                      <option value="RWF">RWF</option>
                      <option value="KES">KES</option>
                      <option value="NGN">NGN</option>
                      <option value="ZAR">ZAR</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold"
              >
                {loading ? 'Saving...' : isEdit ? 'Update Job' : 'Create Job'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/jobs')}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold">Preview</h2>
              <button onClick={() => setPreview(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold capitalize">{formData.employmentType}</span>
                {formData.experienceLevel && <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold capitalize">{formData.experienceLevel}</span>}
                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${formData.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{formData.status}</span>
                {formData.remote && <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-semibold">Remote</span>}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{formData.title || 'Untitled'}</h1>
              {formData.companyName && <p className="text-blue-600 font-medium">{formData.companyName}</p>}
              {(formData.country || formData.city) && (
                <p className="text-gray-500 text-sm">{[formData.city, formData.country].filter(Boolean).join(', ')}</p>
              )}
              {formData.description && <p className="text-gray-700 whitespace-pre-wrap">{formData.description}</p>}
              {formData.requirements && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Requirements</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {formData.requirements.split('\n').filter(r => r.trim()).map((r, i) => (
                      <li key={i} className="text-gray-700 text-sm">{r}</li>
                    ))}
                  </ul>
                </div>
              )}
              {formData.responsibilities && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Responsibilities</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {formData.responsibilities.split('\n').filter(r => r.trim()).map((r, i) => (
                      <li key={i} className="text-gray-700 text-sm">{r}</li>
                    ))}
                  </ul>
                </div>
              )}
              {formData.skills && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.split(',').map(s => s.trim()).filter(s => s).map((s, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {showSalary && formData.minSalary && (
                <p className="text-gray-700 text-sm"><span className="font-medium">Salary:</span> {formData.currency} {formData.minSalary}{formData.maxSalary ? ` – ${formData.maxSalary}` : ' (negotiable)'}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminJobForm;
